<template>
    <div class="bg-white p-3 rounded-lg shadow-md" data-testid="result-template-wizard">
        <div class="flex justify-between items-center mb-3">
            <div>
                <h2 class="text-xl font-bold">New Result Template</h2>
                <p class="text-xs text-gray-500">Guided setup — answer a few questions and I'll build it for you.</p>
            </div>
            <div class="flex items-center gap-3">
                <button @click="$emit('switch-to-builder')"
                    data-testid="btn-wizard-advanced-builder"
                    class="text-xs text-primary hover:underline">
                    Use advanced builder instead
                </button>
                <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700"
                    data-testid="btn-close-template-wizard">
                    <i class="pi pi-times text-xl"></i>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <!-- Chat panel -->
            <div class="lg:col-span-3 flex flex-col border border-gray-200 rounded-lg bg-gray-50/50">
                <!-- Progress -->
                <div class="px-4 pt-3">
                    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div class="h-full bg-primary rounded-full transition-all duration-300"
                            :style="{ width: `${Math.min(100, (progress.answered / progress.total) * 100)}%` }"></div>
                    </div>
                </div>

                <!-- Transcript -->
                <div ref="chatScroll" class="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[20rem] max-h-[26rem]">
                    <template v-for="(entry, index) in transcript" :key="index">
                        <div class="flex">
                            <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm">
                                {{ entry.question }}
                            </div>
                        </div>
                        <div class="flex justify-end">
                            <button @click="jumpBackTo(index)" :disabled="saving"
                                :data-testid="`wizard-answer-${index}`"
                                class="group bg-primary text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm
                                    max-w-[85%] shadow-sm text-left cursor-pointer hover:bg-primary/85 transition-colors"
                                title="Change this answer">
                                {{ entry.answer }}
                                <i class="pi pi-pencil text-[10px] ml-1.5 opacity-0 group-hover:opacity-70 transition-opacity"></i>
                            </button>
                        </div>
                    </template>

                    <!-- Current question -->
                    <div class="flex">
                        <div class="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-700 max-w-[85%] shadow-sm"
                            data-testid="wizard-current-question">
                            {{ currentQuestion }}
                        </div>
                    </div>

                    <!-- Step-specific notices -->
                    <div v-if="currentStep === 'grade' && gradeGroups.length === 0"
                        class="flex">
                        <div class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 max-w-[85%]">
                            <i class="pi pi-exclamation-triangle mr-1"></i>
                            Your school has no grading scale yet, so Grade columns will stay blank on real
                            report cards. Create one on the
                            <RouterLink :to="{ name: 'admin-grade-system' }" target="_blank"
                                class="font-semibold underline hover:text-amber-900"
                                data-testid="wizard-grade-system-link">Grade System</RouterLink>
                            page (opens in a new tab), then link it to this template (Edit → Grading System).
                        </div>
                    </div>
                </div>

                <!-- Input area -->
                <div class="border-t border-gray-200 bg-white rounded-b-lg px-4 py-3">
                    <!-- Step: name -->
                    <div v-if="currentStep === 'name'" class="flex gap-2">
                        <InputText v-model="nameInput" class="flex-1" autofocus
                            placeholder="e.g. Junior Secondary Report Card"
                            data-testid="wizard-input-name" @keyup.enter="answerName" />
                        <Button icon="pi pi-send" :disabled="!nameInput.trim()" @click="answerName"
                            data-testid="wizard-send-name" />
                    </div>

                    <!-- Step: assessments preset -->
                    <div v-else-if="currentStep === 'assessments'" class="flex flex-wrap gap-2">
                        <button v-for="preset in assessmentPresets" :key="preset.id"
                            @click="choosePreset(preset.id)"
                            :data-testid="`wizard-preset-${preset.id}`"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors">
                            <span class="block text-sm font-medium text-gray-800">{{ preset.label }}</span>
                            <span class="block text-[11px] text-gray-500">{{ preset.description }}</span>
                        </button>
                    </div>

                    <!-- Step: assessment details -->
                    <div v-else-if="currentStep === 'assessmentDetails'" class="space-y-2">
                        <div v-for="(entry, index) in answers.assessments" :key="index"
                            class="flex items-center gap-2">
                            <InputText v-model="entry.name" class="flex-1" placeholder="Assessment name"
                                :data-testid="`wizard-assessment-name-${index}`" />
                            <InputNumber v-model="entry.maxScore" :min="1" :max="1000"
                                :disabled="!!entry.existingId" inputClass="w-20"
                                :data-testid="`wizard-assessment-max-${index}`" />
                            <button @click="entry.isExam = !entry.isExam" :disabled="!!entry.existingId"
                                class="text-[11px] px-2 py-1 rounded-full border transition-colors"
                                :class="entry.isExam
                                    ? 'bg-orange-50 text-orange-700 border-orange-200'
                                    : 'bg-primary/10 text-primary-700 border-primary/20'">
                                {{ entry.isExam ? 'Exam' : 'CA' }}
                            </button>
                            <span v-if="entry.existingId"
                                class="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded"
                                v-tooltip.top="'Matches a reusable assessment your school already has — it will be reused, not duplicated.'">
                                reusable
                            </span>
                            <span v-else-if="entry.name.trim()"
                                class="text-[10px] bg-primary/10 text-primary-700 border border-primary/20 px-1.5 py-0.5 rounded"
                                v-tooltip.top="'New — will be created as a reusable assessment for your whole school.'">
                                new · reusable
                            </span>
                            <button @click="removeAssessmentRow(index)" class="text-red-400 hover:text-red-600">
                                <i class="pi pi-trash text-sm"></i>
                            </button>
                        </div>

                        <div class="flex items-center justify-between pt-1">
                            <button @click="addAssessmentRow" class="text-xs text-primary hover:underline">
                                + Add another assessment
                            </button>
                            <span class="text-xs text-gray-500">
                                Scores add up to <strong>{{ detailsMaxTotal }}</strong>
                            </span>
                        </div>

                        <p v-if="detailsError" class="text-xs text-red-500">{{ detailsError }}</p>

                        <div class="flex justify-end">
                            <Button label="Looks good" icon="pi pi-check" size="small"
                                data-testid="wizard-confirm-details" @click="confirmAssessmentDetails" />
                        </div>
                    </div>

                    <!-- Step: total -->
                    <div v-else-if="currentStep === 'total'" class="flex flex-wrap gap-2">
                        <button @click="answerTotal(true)" data-testid="wizard-chip-total-yes" :class="chipClass">
                            Yes, add a Total
                        </button>
                        <button @click="answerTotal(false)" data-testid="wizard-chip-total-no" :class="chipMutedClass">
                            No thanks
                        </button>
                    </div>

                    <!-- Step: grade -->
                    <div v-else-if="currentStep === 'grade'" class="flex flex-wrap gap-2">
                        <button @click="answerGrade('grade_remark')" data-testid="wizard-chip-grade-remark" :class="chipClass">
                            Grade + Remark
                        </button>
                        <button @click="answerGrade('grade')" data-testid="wizard-chip-grade-only" :class="chipClass">
                            Grade only
                        </button>
                        <button @click="answerGrade('none')" data-testid="wizard-chip-grade-none" :class="chipMutedClass">
                            No grades
                        </button>
                    </div>

                    <!-- Step: gpa (tertiary) -->
                    <div v-else-if="currentStep === 'gpa'" class="flex flex-wrap gap-2">
                        <button @click="answerGpa(true)" data-testid="wizard-chip-gpa-yes" :class="chipClass">
                            Yes, add GPA tracking
                        </button>
                        <button @click="answerGpa(false)" data-testid="wizard-chip-gpa-no" :class="chipMutedClass">
                            No GPA columns
                        </button>
                    </div>

                    <!-- Step: grade scale -->
                    <div v-else-if="currentStep === 'gradeScale'" class="flex flex-wrap gap-2">
                        <button v-for="group in gradeGroups" :key="group.id"
                            @click="chooseGradeScale(group)"
                            :data-testid="`wizard-grade-scale-${group.id}`"
                            :class="chipClass">
                            {{ group.name }}
                        </button>
                    </div>

                    <!-- Step: feedbacks -->
                    <div v-else-if="currentStep === 'feedbacks'" class="space-y-2">
                        <div class="flex flex-wrap gap-2">
                            <button v-for="option in feedbackOptions" :key="option.label"
                                @click="toggleFeedback(option.label)"
                                class="text-xs px-3 py-1.5 rounded-full border transition-colors"
                                :class="selectedFeedbackLabels.includes(option.label)
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary'">
                                <i v-if="selectedFeedbackLabels.includes(option.label)" class="pi pi-check mr-1 text-[10px]"></i>
                                {{ option.label }}
                            </button>
                        </div>
                        <div class="flex gap-2">
                            <InputText v-model="customFeedbackInput" class="flex-1" size="small"
                                placeholder="Add your own… e.g. Guardian's Comment"
                                @keyup.enter="addCustomFeedback" />
                            <Button label="Add" size="small" text :disabled="!customFeedbackInput.trim()"
                                @click="addCustomFeedback" />
                        </div>
                        <div class="flex justify-end">
                            <Button label="Continue" icon="pi pi-arrow-right" iconPos="right" size="small"
                                data-testid="wizard-confirm-feedbacks" @click="confirmFeedbacks" />
                        </div>
                    </div>

                    <!-- Step: style -->
                    <div v-else-if="currentStep === 'style'" class="flex flex-wrap gap-2">
                        <button v-for="option in styleOptions" :key="option.value"
                            @click="chooseStyle(option.value)"
                            :data-testid="`wizard-style-${option.value}`"
                            class="text-left border border-gray-300 rounded-xl px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors">
                            <span class="block text-sm font-medium text-gray-800">{{ option.label }}</span>
                            <span class="block text-[11px] text-gray-500">{{ option.description }}</span>
                        </button>
                    </div>

                    <!-- Step: review -->
                    <div v-else-if="currentStep === 'review'" class="space-y-3">
                        <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                            <p><span class="text-gray-500">Template:</span> <strong>{{ answers.name }}</strong></p>
                            <p>
                                <span class="text-gray-500">Columns:</span>
                                {{ reviewColumns.map((c) => c.label).join(' · ') }}
                            </p>
                            <p v-if="answers.feedbacks.length > 0">
                                <span class="text-gray-500">Comments:</span>
                                {{ answers.feedbacks.map((f) => f.label).join(', ') }}
                            </p>
                            <p v-if="assessmentsToCreate.length > 0" class="text-xs text-primary-700">
                                <i class="pi pi-plus-circle mr-1"></i>
                                New reusable assessments will be created: {{ assessmentsToCreate.join(', ') }}
                            </p>
                            <p v-if="assessmentsToReuse.length > 0" class="text-xs text-emerald-700">
                                <i class="pi pi-refresh mr-1"></i>
                                Reusing existing assessments: {{ assessmentsToReuse.join(', ') }}
                            </p>
                        </div>

                        <div v-if="showGradeScaleWarning"
                            class="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                            <i class="pi pi-exclamation-triangle mr-1"></i>
                            No grading scale is linked yet — Grade columns will stay blank until you create a
                            scale on the
                            <RouterLink :to="{ name: 'admin-grade-system' }" target="_blank"
                                class="font-semibold underline hover:text-amber-900">Grade System</RouterLink>
                            page and link it to this template.
                        </div>

                        <label v-if="answers.includeTotal && !isTertiary" class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <Checkbox v-model="answers.includeOverallPercentage" :binary="true" />
                            Also show an overall percentage summary
                        </label>

                        <div class="flex justify-end">
                            <Button :label="saving ? 'Creating…' : 'Create Template'" icon="pi pi-check"
                                :loading="saving" data-testid="btn-wizard-create-template" @click="saveTemplate" />
                        </div>
                    </div>

                    <!-- Back -->
                    <div v-if="transcript.length > 0 && !saving" class="mt-2 flex items-center justify-between">
                        <button @click="goBack" class="text-[11px] text-gray-400 hover:text-gray-600"
                            data-testid="wizard-back">
                            <i class="pi pi-undo mr-1"></i>Back to previous question
                        </button>
                        <span class="text-[11px] text-gray-300">or click any answer above to change it</span>
                    </div>
                </div>
            </div>

            <!-- Live preview -->
            <div class="lg:col-span-2">
                <WizardPreviewCard
                    :templateName="answers.name"
                    :columns="previewColumns"
                    :summaries="previewSummaries"
                    :feedbacks="previewAnswersFeedbacks"
                    :styleCode="answers.styleCode"
                    :gradeBands="selectedGradeBands" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useResultTemplateWizard } from '@/composables/useResultTemplateWizard';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import WizardPreviewCard from './WizardPreviewCard.vue';

const emit = defineEmits(['close', 'saved', 'switch-to-builder']);

const { isTertiary } = useSchoolFeatures();

const {
  answers,
  transcript,
  saving,
  nameInput,
  detailsError,
  selectedFeedbackLabels,
  customFeedbackInput,
  currentStep,
  currentQuestion,
  progress,
  gradeGroups,
  feedbackOptions,
  styleOptions,
  assessmentPresets,
  previewColumns,
  previewSummaries,
  reviewColumns,
  selectedGradeBands,
  assessmentsToCreate,
  assessmentsToReuse,
  detailsMaxTotal,
  showGradeScaleWarning,
  answerName,
  choosePreset,
  addAssessmentRow,
  removeAssessmentRow,
  confirmAssessmentDetails,
  answerTotal,
  answerGrade,
  answerGpa,
  chooseGradeScale,
  toggleFeedback,
  addCustomFeedback,
  confirmFeedbacks,
  chooseStyle,
  goBack,
  jumpBackTo,
  saveTemplate,
} = useResultTemplateWizard(emit, isTertiary);

// Feedback boxes only appear on the preview once the feedbacks step is answered.
const previewAnswersFeedbacks = computed(() =>
  transcript.value.some((t) => t.stepId === 'feedbacks') ? answers.feedbacks : [],
);

const chipClass = 'text-sm px-3 py-1.5 rounded-full border border-primary/40 text-primary bg-primary/5 hover:bg-primary hover:text-white transition-colors';
const chipMutedClass = 'text-sm px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 transition-colors';

// Keep the newest message in view as the conversation grows.
const chatScroll = ref<HTMLElement | null>(null);
watch(
  () => [transcript.value.length, currentStep.value],
  async() => {
    await nextTick();
    chatScroll.value?.scrollTo({ top: chatScroll.value.scrollHeight, behavior: 'smooth' });
  },
);
</script>
